import { apiCancelOrder, apiCancelOrderByGuess } from "@/api/api";
import { toast } from "@/components/ui/showToast";
import { get } from "@/utils/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toNumber } from "../positions-panel-components/utils";

/**
 * 取消订单业务逻辑 Hook
 *
 * 功能：
 * - 管理取消订单的确认对话框状态
 * - 处理取消单个或全部订单的逻辑
 * - 调用 API 执行取消操作
 * - 显示成功/失败提示
 * - 刷新订单列表
 *
 * @param guessNo - 竞猜编号
 * @param userId - 用户ID
 * @returns 对话框状态、加载状态和处理函数
 */
export function useCancelOrder( guessNo: string, userId: number | null ) {
    const queryClient = useQueryClient();
    const [ confirmDialogOpen, setConfirmDialogOpen ] = useState( false );
    const [ cancelAllDialogOpen, setCancelAllDialogOpen ] = useState( false );
    const [ pendingCancelAll, setPendingCancelAll ] = useState( false );
    const [ pendingCancelOrder, setPendingCancelOrder ] = useState<any | null>( null );

    const cancelOrderMutation = useMutation( {
        mutationFn: (
            params: { orderNo: string; orderNoType: 1 | 2; guessType?: 1 | 2; price?: number; direction?: 1 | 2 },
        ) => apiCancelOrder( params ),
        onSuccess: () => {
            queryClient.invalidateQueries( { queryKey: [ "guess-open-orders", guessNo, userId ] } );
        },
    } );

    const cancelAllMutation = useMutation( {
        mutationFn: ( params: { guessNo: string } ) => apiCancelOrderByGuess( params ),
        onSuccess: () => {
            queryClient.invalidateQueries( { queryKey: [ "guess-open-orders", guessNo, userId ] } );
        },
    } );

    const handleCancelOrder = ( order: any ) => {
        setPendingCancelOrder( order );
        setPendingCancelAll( false );
        setConfirmDialogOpen( true );
    };

    const handleCancelAll = () => {
        setPendingCancelOrder( null );
        setPendingCancelAll( true );
        setCancelAllDialogOpen( true );
    };

    const handleConfirmCancel = async () => {
        if ( !pendingCancelOrder ) return;
        try {
            const betType = toNumber(
                get( pendingCancelOrder, "betType", get( pendingCancelOrder, "guessType", 1 ) ),
                1,
            );
            const price = toNumber( get( pendingCancelOrder, "price", 0 ) );
            const [ isOk, res ] = await cancelOrderMutation.mutateAsync( {
                orderNo: guessNo,
                orderNoType: 2,
                guessType: betType as 1 | 2,
                direction: toNumber( get( pendingCancelOrder, "direction", 1 ), 1 ) as 1 | 2,
                price,
            } );
            if ( isOk ) {
                toast.success( "Order canceled" );
                setConfirmDialogOpen( false );
                setPendingCancelOrder( null );
            } else {
                toast.error( get( res, "msg", "Failed to cancel order" ) );
            }
        } catch ( error ) {
            toast.error( "Failed to cancel order" );
        }
    };

    const handleConfirmCancelAll = async () => {
        try {
            const [ isOk, res ] = await cancelAllMutation.mutateAsync( { guessNo } );
            if ( isOk ) {
                toast.success( "All orders canceled" );
                setCancelAllDialogOpen( false );
                setPendingCancelAll( false );
            } else {
                toast.error( get( res, "msg", "Failed to cancel orders" ) );
            }
        } catch ( error ) {
            toast.error( "Failed to cancel orders" );
        }
    };

    const handleDirectCancelOrder = async ( order: any ) => {
        try {
            const betType = toNumber(
                get( order, "betType", get( order, "guessType", 1 ) ),
                1,
            );
            const price = toNumber( get( order, "price", 0 ) );
            const [ isOk, res ] = await cancelOrderMutation.mutateAsync( {
                orderNo: guessNo,
                orderNoType: 2,
                guessType: betType as 1 | 2,
                direction: toNumber( get( order, "direction", 1 ), 1 ) as 1 | 2,
                price,
            } );
            if ( isOk ) {
                toast.success( "Order canceled" );
            } else {
                toast.error( get( res, "msg", "Failed to cancel order" ) );
            }
        } catch ( error ) {
            toast.error( "Failed to cancel order" );
        }
    };

    return {
        // 单个取消
        confirmDialogOpen,
        setConfirmDialogOpen,
        isPending: cancelOrderMutation.isPending,
        handleCancelOrder,
        handleConfirmCancel,
        handleDirectCancelOrder,
        setPendingCancelOrder,
        // 全部取消
        cancelAllDialogOpen,
        setCancelAllDialogOpen,
        pendingCancelAll,
        isPendingCancelAll: cancelAllMutation.isPending,
        handleCancelAll,
        handleConfirmCancelAll,
    };
}
